import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

export const UndismissCommentMutationGQL = graphql(/* GraphQL */ `
  mutation UndismissComment($input: UndismissCommentInput!) {
    undismissComment(input: $input) {
      __typename
      ... on UndismissCommentSuccess {
        comment {
          ... on BaseComment {
            __typename
            id
            status
            text
            updatedAt
            parentId
          }
          ... on CommentFromThirdParty {
            labels
          }
          ... on CommentFromBuffer {
            publishingStatus {
              status
              errorMessage
            }
          }
        }
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export type UndismissCommentMutation = ResultOf<
  typeof UndismissCommentMutationGQL
>

type UseUndismissCommentResult = {
  undismissComment: (commentId: string) => Promise<boolean>
  loading: boolean
}

export function useUndismissComment(): UseUndismissCommentResult {
  const [mutate, { loading }] = useTypedMutation(
    UndismissCommentMutationGQL,
    (data) => data.undismissComment,
    {
      successTypename: 'UndismissCommentSuccess',
    },
  )

  const undismissComment = async (commentId: string): Promise<boolean> => {
    try {
      const result = await mutate({
        variables: {
          input: {
            commentId,
          },
        },
      })

      if (result.success) {
        toast.success('Comment reopened successfully', {
          duration: 1_000,
        })
        return true
      } else {
        // eslint-disable-next-line no-console
        console.error('Error reopening comment:', result.error)
        toast.error('An error occurred while reopening the comment', {
          duration: 2_000,
        })
        return false
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error reopening comment:', error)
      toast.error('An error occurred while reopening the comment', {
        duration: 2_000,
      })
      return false
    }
  }

  return {
    undismissComment,
    loading,
  }
}
